import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, TextField,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GavelIcon from '@mui/icons-material/Gavel';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useSnackbar } from 'notistack';
import { useEditMode } from './EditModeContext';
import { getLogsUrl } from '../../utils/verificationAdmin';
import axios from '../../auth/axiosConfig';

const ActionBar = ({ verificationId, verificationStatus, onStatusChange, onRefresh }) => {
  const { isEditMode, exitEditMode } = useEditMode();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forceMatchOpen, setForceMatchOpen] = useState(false);
  const [stateId, setStateId] = useState('');
  const [jurisdictionCode, setJurisdictionCode] = useState('');

  if (!isEditMode) return null;

  const logsUrl = getLogsUrl(verificationId);

  const handleAction = async (action, label, body) => {
    setLoading(true);
    try {
      const response = await axios.post(`/ui/${verificationId}/${action}`, body);
      enqueueSnackbar(response.data.message || `${label} successful`, { variant: 'success' });
      if (onStatusChange && response.data.status) onStatusChange(response.data.status.toUpperCase());
      exitEditMode();
      if (onRefresh) onRefresh();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.detail || `Failed to ${label.toLowerCase()}`, { variant: 'error' });
    } finally {
      setLoading(false);
      setConfirmAction(null);
      setForceMatchOpen(false);
    }
  };

  const handleForceMatch = () => {
    const sid = stateId.trim();
    const jc = jurisdictionCode.trim().toLowerCase();
    if (!sid || !jc) {
      enqueueSnackbar('state_id and jurisdiction_code are required', { variant: 'error' });
      return;
    }
    // Defer format validation to the backend, which has the canonical
    // OPENCORPORATES_TO_US_STATE_CODES list and accepts non-US codes like
    // 'pr' and 'gb' that wouldn't match a simple us_xx regex.
    handleAction('force-match', 'Force match', { state_id: sid, jurisdiction_code: jc });
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 1,
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e0e0e0',
          flexWrap: 'wrap',
        }}
      >
        <Chip
          label={verificationStatus}
          size="small"
          color={verificationStatus === 'COMPLETED' ? 'success' : verificationStatus === 'READY_FOR_REVIEW' ? 'warning' : 'default'}
          sx={{ mr: 1 }}
        />

        <Button
          size="small"
          variant="text"
          startIcon={<OpenInNewIcon />}
          href={logsUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Logs
        </Button>

        {verificationStatus === 'READY_FOR_REVIEW' && (
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={() => setConfirmAction({
              action: 'mark-complete',
              label: 'Mark complete',
              description: 'This will mark the verification as COMPLETED and make it visible to the customer.',
            })}
            disabled={loading}
            sx={{ backgroundColor: '#2e7d32' }}
          >
            Mark done
          </Button>
        )}

        {(verificationStatus === 'NO_MATCH' || verificationStatus === 'FAILED') && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<RateReviewIcon />}
            onClick={() => setConfirmAction({
              action: 'mark-ready-for-review',
              label: 'Mark ready for review',
              description: `This will move the verification from ${verificationStatus} to READY_FOR_REVIEW so the existing detail data can be reviewed.`,
            })}
            disabled={loading}
          >
            Ready for review
          </Button>
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={<LockIcon />}
          onClick={() => setConfirmAction({ action: 'freeze', label: 'Freeze', description: 'This will freeze the verification, preventing any further modifications.' })}
          disabled={loading}
          sx={{ borderColor: '#ed6c02', color: '#ed6c02' }}
        >
          Freeze
        </Button>

        {verificationStatus === 'COMPLETED' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOffIcon />}
            onClick={() => setConfirmAction({ action: 'hide', label: 'Hide', description: 'This will set the verification back to READY_FOR_REVIEW, hiding it from the customer.' })}
            disabled={loading}
          >
            Hide
          </Button>
        )}

        {verificationStatus !== 'COMPLETED' && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<GavelIcon />}
              onClick={() => { setStateId(''); setJurisdictionCode(''); setForceMatchOpen(true); }}
              disabled={loading}
            >
              Force match
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<BlockIcon />}
              onClick={() => setConfirmAction({
                action: 'mark-no-match',
                label: 'Mark NO_MATCH',
                description: 'This will flip the verification\'s status to NO_MATCH and clear its detail_data, matched entity, and force-match fields. A snapshot of the current detail_data is written to edit history first.',
              })}
              disabled={loading}
            >
              No match
            </Button>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={() => setConfirmAction({
                action: 'restart',
                label: 'Restart',
                description: 'This will re-queue the verification (status → PENDING). The processor will pick it up and reprocess from scratch, overwriting the current result.',
              })}
              disabled={loading}
            >
              Restart
            </Button>
          </>
        )}
      </Box>

      <Dialog open={forceMatchOpen} onClose={() => setForceMatchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Force match</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set the company state id and jurisdiction code. The verification will be
            re-queued (PENDING) for the processor and overwrite the current result.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="State id (company number)"
            value={stateId}
            onChange={(e) => setStateId(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Jurisdiction code"
            value={jurisdictionCode}
            onChange={(e) => setJurisdictionCode(e.target.value)}
            placeholder="e.g. us_ca"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceMatchOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={loading} onClick={handleForceMatch}>
            {loading ? 'Submitting…' : 'Force match'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmAction?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmAction?.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => confirmAction && handleAction(confirmAction.action, confirmAction.label, undefined)}
            disabled={loading || !confirmAction}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionBar;
